/**
 * Core API Client Service
 * 
 * Handles low-level API communication with automatic version routing,
 * header management, and performance monitoring.
 * 
 * This is the foundation layer that all other data services use for
 * making HTTP requests to the Tower Hunt backend.
 */

import { defineStore } from 'pinia'
import { useApiVersioning } from '@/stores/apiVersioning'
import { usePerformanceMonitoring } from '@/composables/performanceMonitoring'
import { useNotificationsStore } from '@/stores/notifications'
import { useStepUpStore } from '@/stores/stepUp'
import { pushDiagnostic, readRequestId } from '@/services/clientDiagnostics'
import { isExpectedRefusal } from '@/utils/expectedRefusal'
import {
  RENEWAL_HEADER,
  adoptRenewedSessionToken,
  bearerOf,
  handleSessionExpired,
  isRequestWithheld,
  isSessionExpired,
  neverAnswered,
  readHeader,
} from '@/services/sessionToken'

/**
 * Does the caller read this HTTP status as an answer (`options.expectedStatuses`)?
 * A network failure carries no status and is never one.
 */
function isExpectedStatus(options, status) {
  return status !== undefined && Array.isArray(options?.expectedStatuses) && options.expectedStatuses.includes(status)
}

/**
 * The query string a GET's or DELETE's `options.query` names: `?` and every
 * entry whose value is neither null nor undefined, in the object's order (an
 * array value appends once per element, so `{ 'paths[]': paths }` repeats the
 * key), or '' when no entry is left, so a URL never ends in a bare `?`. THE ONE QUERY
 * BUILD of this client: a wrapper names its params instead of building a
 * `URLSearchParams` of its own, which keeps the bytes of every optional param
 * off the public pages' boot path (golden #71, the build-smoke budget).
 */
function queryString(query) {
  const params = new URLSearchParams()
  for (const key in query) {
    for (const each of [query[key]].flat()) if (each != null) params.append(key, each)
  }
  const text = params.toString()
  return text && `?${text}`
}

export const useApiClient = defineStore('apiClient', () => {
  const apiVersioning = useApiVersioning()
  const performanceMonitoring = usePerformanceMonitoring()
  
  /**
   * Base fetch method with automatic API version routing and intelligent header merging
   *
   * Endpoint path conventions (enforced by `bin/audit-api-paths`):
   *   - MUST start with a leading slash. `apiVersioning.buildApiUrl` does raw string
   *     concatenation (`/api/v2` + endpoint), so a missing slash collapses the
   *     prefix into the next segment: `api.get("whoami")` → `/api/v2whoami` → 404.
   *   - MUST NOT include the `/api/v{N}` prefix. It is already prepended;
   *     hard-coding it produces `/api/v2/api/v2/...` → 404.
   *
   * Copy paths from controller routes as version-relative — e.g. for
   * `PATCH /api/v2/discussions/hub_groups/:id`, pass `/discussions/hub_groups/${id}`.
   *
   * @param {string} endpoint - The version-relative API path (e.g., '/properties/123')
   * @param {Object} options - Fetch options including method, body, headers, etc.
   *   Non-fetch options this layer consumes (`fetch` ignores unknown RequestInit
   *   keys, which is what lets them ride here): `silent403`, `_stepUpRetried`,
   *   `withStatus`, `absolute`, `raw`, `session`, and `expectedStatuses`.
   * @returns {Promise<Object|Response>} The parsed JSON response from the API —
   *   or, when `options.withStatus` is set, `{ status, body }`; or the
   *   `Response` itself when `options.raw` is set.
   * @throws {Error} If the request fails or returns non-OK status
   *
   * WHY `withStatus` EXISTS. A resolved `baseFetch` normally hands back the parsed
   * body and nothing else, so every 2xx reads alike — the caller cannot tell "here
   * is your created record" from "understood, but nothing was persisted". That is
   * fine until an endpoint has TWO success arms, which is exactly the shape of the
   * reference-visibility gate: the four message-create paths answer 201 with a
   * message or 202 with a remediation card and NO message
   * (`ReferenceVisibilityGateConcern`). Discriminating on the body's shape would
   * make the branch depend on a response key rather than on what the server said,
   * so the arm the client takes is opted into per call site here instead.
   *
   * THE WRAPPER KEY IS `body`, NOT `data`, AND THAT NAME IS LOAD-BEARING. What
   * `withStatus` wraps is the WHOLE parsed body — `{ success, data, cursors }` for
   * every controller that renders the house envelope — so a wrapper keyed `data`
   * put a second `data` one level above the server's own. Consumers written before
   * `withStatus` existed read `response.data.<payload>` and kept resolving: they
   * silently got the envelope instead of the payload, one layer too shallow, with
   * no error anywhere because both layers are objects. Naming it `body` makes that
   * confusion structurally impossible and turns any missed call site into a loud
   * `undefined` rather than a plausible wrong object. Payload access is therefore
   * always `response.body.data`.
   *
   * WHY `absolute` AND `raw` EXIST — ONE CLIENT, NOT THREE. Some call sites
   * cannot state their request in the default shape, and would otherwise
   * hand-roll their own `fetch`. A hand-rolled fetch pushes nothing into the
   * diagnostics ring, so a failed policy-page load or a failed tour narration
   * would leave no trace anywhere — exactly the silent breakage a member
   * reports as "it just stopped working" (artemis-product-help design §D11).
   * Both options are opt-in and default-off, so every other caller is
   * byte-identical.
   *
   *   - `absolute: true` — the endpoint is used VERBATIM as the URL, skipping
   *     `apiVersioning.buildApiUrl`'s `/api/v2` prefix, for a path that lives
   *     outside the v2 namespace (the Artemis bot policy page, an imagery
   *     session endpoint, a standing permission's revoke path). The prefix
   *     rule itself is untouched: `buildApiUrl` still
   *     returns `/api/v2` + endpoint unconditionally for everyone else, which
   *     is what its own pin asserts.
   *   - `raw: true` — resolve to the `Response` itself instead of parsing it as
   *     JSON, and let CALLER headers win the header merge. The guided-tour
   *     voice streamer reads `audio/mpeg` through `response.arrayBuffer()`, so
   *     `response.json()` would throw on binary audio and the system's
   *     `Accept: application/json` would ask the server for the wrong
   *     representation of its own response. A raw caller owns its wire
   *     contract; it does NOT get to skip the failure path — the option is read
   *     BELOW the `!response.ok` block, so the `api_error` push, the step-up
   *     retry and the 403 toast all still run exactly as they do for a JSON
   *     call. `withStatus` is meaningless alongside `raw` (a `Response` already
   *     carries its own `status`).
   *
   * WHY `session` EXISTS. The sign-in door holds a freshly minted session
   * WITHOUT adopting it while the passkey offer is open (passwordless-sign-in
   * design D12), and the offer's requests must act as that held identity, not
   * as the tab's. `session: { bearer, stepUpToken }` pins both identity headers
   * over the system ones: `Authorization: Bearer <bearer>`, and
   * `X-Step-Up-Token` when `stepUpToken` is given. Otherwise the tab's cached
   * proof is dropped, since it belongs to the tab's identity. Without the pin,
   * the system bearer (whatever localStorage holds, which another tab may have
   * written) and any cached proof would win the merge. A pinned request is the
   * caller's alone: it never adopts an `X-Session-Token` renewal, a
   * `401 session_expired` never reaches `handleSessionExpired`, and a
   * `step_up_required` 403 never opens the re-verify prompt or a toast. Each
   * rejects to the caller as the ordinary error, because none of them says
   * anything about the tab's own session, and the tab's handlers would tear
   * down the door that holds this one.
   *
   * WHY `expectedStatuses` EXISTS. Some routes answer a question with a non-2xx
   * status by contract — `GET /validation_queue/groups/:key` answers "that group
   * is no longer yours to review" with a 404, deliberately (th-lx10vi) — and the
   * caller handles that status as the answer, not as a failure. Logging it as
   * "API request failed" puts a console error on a happy path and pushes an
   * `api_error` that evicts a real failure from the 20-slot diagnostics ring.
   * `expectedStatuses: [404]` names the statuses that are answers: the request
   * still REJECTS with the same `error.status` / `code` / `details`, so the
   * caller's branch is unchanged, but nothing is logged and nothing enters the
   * ring. Every other status still runs the full failure path, and the
   * session-expired exit, the step-up handshake and the 403 toast (`silent403`
   * is that toast's own opt-out) are untouched by it.
   *
   * AN EXPECTED REFUSAL IS QUIET ON EVERY ROUTE, with no option. A typed refusal
   * the client handles on its own (`utils/expectedRefusal.js`: a dead result's
   * 422 `result_expired`, which the one handler re-queries on) is an answer for
   * whichever reader meets it, so it rejects exactly as above, unlogged and
   * unrecorded, by its KIND, where `expectedStatuses` names a status for one call.
   */
  const baseFetch = async (endpoint, options = {}) => {
    // A page committed to a full page load sends nothing but a `keepalive`
    // teardown (th-0qphb, `services/sessionToken.js#leavePage`): its answer
    // would render into a page nobody will see, after the server ran — and
    // billed — the read. Held here, before a timer starts or a URL is built,
    // so every caller is covered whatever issued the read.
    if (isRequestWithheld(options)) return neverAnswered()

    // `absolute`: the endpoint IS the URL. See the options note above.
    const apiUrl = options.absolute ? endpoint : apiVersioning.buildApiUrl(endpoint, options)

    // Get system configuration (includes Authorization, API version headers, etc.)
    const systemConfig = apiVersioning.getRequestConfig(options)

    // Intelligently merge headers - system headers take precedence for security,
    // EXCEPT on a `raw` request, where the caller owns the wire contract: it is
    // asking for a representation this layer does not parse, so the Accept it
    // sets is the whole point of the call.
    const mergedHeaders = options.raw
      ? {
          ...systemConfig.headers,  // System headers (Authorization, API versioning)
          ...options.headers        // Caller headers WIN on a raw request
        }
      : {
          ...options.headers,       // Caller headers (Content-Type, custom headers)
          ...systemConfig.headers   // System headers (Authorization, API versioning) - take precedence
        }

    // `session`: a held identity pins both identity headers, applied LAST so it
    // wins on a raw request too. See the options note above.
    if (options.session) {
      delete mergedHeaders['X-Step-Up-Token']
      mergedHeaders.Authorization = `Bearer ${options.session.bearer}`
      if (options.session.stepUpToken) mergedHeaders['X-Step-Up-Token'] = options.session.stepUpToken
    }

    // Build final request configuration
    const requestConfig = {
      ...systemConfig,    // System config (method defaults, etc.)
      ...options,         // Caller options (method, body, etc.)
      headers: mergedHeaders  // Properly merged headers
    }
    
    const timingId = performanceMonitoring.startTiming('api_request')
    
    try {
      const response = await fetch(apiUrl, requestConfig)

      // The sliding session (th-vfii1): a bearer past half its life comes back
      // renewed in a response header, on a success or a failure alike. Adopted
      // only while the bearer THIS request carried is still the one held. A
      // pinned `session`'s bearer is its caller's, never the tab's.
      if (!options.session) {
        adoptRenewedSessionToken(
          readHeader(response.headers, RENEWAL_HEADER),
          bearerOf(mergedHeaders.Authorization),
        )
      }

      if (!response.ok) {
        // Parse error response body to preserve details (e.g., insufficient_funds)
        let errorDetails = {}
        try {
          errorDetails = await response.json()
        } catch {
          // Response body wasn't JSON, that's fine
        }

        const error = new Error(errorDetails.message || `HTTP error! status: ${response.status}`)
        error.status = response.status
        error.code = errorDetails.error  // e.g., 'insufficient_funds'
        error.details = errorDetails      // { shortfall, available_balance, required_cost, etc. }

        // `expectedStatuses`: the caller reads this status as its answer. It
        // still rejects with the error above, unrecorded and unlogged. An
        // expected refusal is the same, by its kind. See the options note above.
        if (isExpectedStatus(options, response.status) || isExpectedRefusal(error)) throw error

        // Record the failure HERE, at the TOP of this block, because the block
        // has THREE exits and only one of them is the plain `throw` at its
        // close: the step-up branch below either RETURNS a retried request or
        // throws on cancellation, and the 403 toast branch falls through. A
        // push placed lower would miss every step-up recovery and every
        // permission toast — precisely the two failure classes a confused
        // member reports. A 403 whose retry succeeds still belongs in the ring.
        // `readRequestId` is used rather than a direct `response.headers.get`
        // so a response double without `headers` cannot raise inside this
        // `try` and be misread as a network failure.
        pushDiagnostic({
          kind: 'api_error',
          path: endpoint,
          method: requestConfig.method || 'GET',
          status: response.status,
          code: errorDetails.error,
          request_id: readRequestId(response),
          message: error.message,
        })

        // A dead session (expired, revoked, a pre-th-vfii1 token) is not an
        // error this caller can recover from: the tab leaves the identity and
        // goes to sign-in, once, however many requests answer this at the same
        // moment (`services/sessionToken.js#handleSessionExpired`). No toast —
        // the page is about to change under it. A pinned `session` is not the
        // tab's, so its dead bearer rejects to the caller like any failure;
        // nor is a bearer the tab has since replaced (th-m1z22).
        if (!options.session && isSessionExpired(response.status, errorDetails.error)) {
          handleSessionExpired(bearerOf(mergedHeaders.Authorization))
          throw error
        }

        // A `step_up_required` 403 is a recoverable re-verify prompt, not a
        // denial. Obtain a fresh proof (single-flight via the store), then
        // re-issue the original request ONCE — getRequestConfig injects the
        // cached X-Step-Up-Token on the retry. `_stepUpRetried` bounds this to
        // one re-issue; a second step_up_required falls through to the normal
        // path. On either branch we must NOT show the generic 'Permission
        // denied' toast. useStepUpStore() is called lazily here (never at
        // module top level) to avoid the stepUp↔apiClient init cycle.
        if (response.status === 403 && errorDetails.error === 'step_up_required' && !options._stepUpRetried) {
          // A pinned `session` carries its own proof, and the tab's prompt
          // would mint one for the wrong identity: reject to the caller, with
          // no prompt and no toast, and leave the tab's cached proof alone.
          if (options.session) throw error

          // The server REJECTED whatever proof rode this request, so any cached
          // proof is now stale and MUST be dropped before re-acquiring. Without
          // this, obtainProof() would short-circuit on a still-unexpired-but-dead
          // token (hasValidProof stays true: only the server-side state changed)
          // and replay it straight into a second 403. This is the one-time-use
          // payout case (th-lqw5c): the first payout consumes the proof's jti, so
          // a second payout within the 15-min window re-presents a consumed token
          // — clearing forces a real re-mint instead of wedging the user. Safe for
          // reusable proofs too: a step_up_required 403 means the token was
          // invalid regardless, so discarding it is always correct.
          const stepUp = useStepUpStore()
          stepUp.clear()
          const proof = await stepUp.obtainProof()
          if (proof) {
            return baseFetch(endpoint, { ...options, _stepUpRetried: true })
          }
          // User cancelled / no proof: surface the structured error without the
          // generic toast so callers can react to the cancellation.
          throw error
        }

        // Surface 403s as toasts so silent permission denials don't look like dead UI.
        // Callers that want to handle this themselves can pass `silent403: true`.
        // A step_up_required that survives the one replay is the caller's to
        // phrase (useGatedWrite's one refusal sentence), never a raw toast here.
        if (response.status === 403 && !options.silent403 && errorDetails.error !== 'step_up_required') {
          const message = typeof errorDetails.error === 'string' && errorDetails.error !== 'Forbidden'
            ? errorDetails.error
            : "You don't have permission to perform this action."
          useNotificationsStore().addDataProcessingNotification(
            'error',
            'Permission denied',
            message,
            5,
          )
        }

        throw error
      }

      // `raw`: hand the Response back unparsed. Placed BELOW the `!response.ok`
      // block so a raw request's failures are recorded, retried and toasted
      // exactly like every other request's, and ABOVE the 204 branch because a
      // raw caller reads the body itself — `response.arrayBuffer()` on an empty
      // body is a zero-length buffer, not the parse error `response.json()`
      // raises there.
      if (options.raw) {
        const rawDuration = performanceMonitoring.endTiming(timingId)
        performanceMonitoring.recordMetric('api_response', endpoint.split('/')[1], rawDuration)
        return response
      }

      // 204 No Content: the response body is empty by spec, so calling
      // `response.json()` rejects with SyntaxError. Callers that want to
      // distinguish 204 (e.g. opt-out gate on
      // `POST /api/v2/map_presence`) from a real network failure rely on
      // this branch — return null so the resolved value is explicit rather
      // than a thrown parse error caught one layer up as a generic failure.
      if (response.status === 204) {
        const duration = performanceMonitoring.endTiming(timingId)
        performanceMonitoring.recordMetric('api_response', endpoint.split('/')[1], duration)
        return options.withStatus ? { status: 204, body: null } : null
      }

      const body = await response.json()
      const duration = performanceMonitoring.endTiming(timingId)

      // Log performance for monitoring
      performanceMonitoring.recordMetric('api_response', endpoint.split('/')[1], duration)

      return options.withStatus ? { status: response.status, body } : body
    } catch (error) {
      performanceMonitoring.endTiming(timingId)
      if (error.name !== 'AbortError' && !isExpectedStatus(options, error?.status) && !isExpectedRefusal(error)) {
        console.error(`API request failed: ${endpoint}`, error)

        // The AbortError rule lives in ONE place in this function — inside
        // this guard — because the app aborts in flight on every navigation
        // and every debounced refilter, and recording those would fill a
        // 20-slot ring with noise that evicts the real failure a report needs.
        //
        // `status === undefined` is what separates a network failure from an
        // HTTP one. This catch also sees the `throw error` that closes the
        // `!response.ok` block (and, through the `return baseFetch(...)`
        // retry, a second step-up 403 raised one frame down), and those
        // already pushed an `api_error` above. Without this test each HTTP
        // failure would be double-counted under the wrong kind.
        if (error?.status === undefined) {
          pushDiagnostic({
            kind: 'network_error',
            path: endpoint,
            method: requestConfig.method || 'GET',
            message: error?.message,
          })
        }
      }
      throw error
    }
  }
  
  /**
   * Convenience method for GET requests. `options.query` is the request's
   * params as an object, appended to the endpoint by `queryString` (a null or
   * undefined value is left out); the endpoint `baseFetch` sees, and the
   * diagnostics ring records, is the whole URL as before.
   */
  const get = async (endpoint, { query, ...options } = {}) => {
    return baseFetch(endpoint + queryString(query), { ...options, method: 'GET' })
  }
  
  /**
   * Convenience method for POST requests
   */
  const post = async (endpoint, body, options = {}) => {
    return baseFetch(endpoint, {
      ...options,
      method: 'POST',
      body: typeof body === 'string' ? body : JSON.stringify(body)
    })
  }
  
  /**
   * Convenience method for PATCH requests
   */
  const patch = async (endpoint, body, options = {}) => {
    return baseFetch(endpoint, {
      ...options,
      method: 'PATCH',
      body: typeof body === 'string' ? body : JSON.stringify(body)
    })
  }

  /**
   * Convenience method for PUT requests
   */
  const put = async (endpoint, body, options = {}) => {
    return baseFetch(endpoint, {
      ...options,
      method: 'PUT',
      body: typeof body === 'string' ? body : JSON.stringify(body)
    })
  }
  
  /**
   * Convenience method for DELETE requests; `options.query` as for `get`.
   */
  const del = async (endpoint, { query, ...options } = {}) => {
    return baseFetch(endpoint + queryString(query), { ...options, method: 'DELETE' })
  }

  /**
   * Typed wrappers for the actor-map-presence-v2 endpoints
   * (`Api::V2::MapPresenceController` — `POST /api/v2/map_presence`,
   * `GET /api/v2/map_presence`). Used by the `mapPresence` Pinia store's
   * `writeViewport` (debounced pan-write) and `fetchSnapshot` (initial
   * viewport hydrate on store init / map-route-entry / sign-in).
   *
   * Bead: th-1yvjd.12. See openspec/changes/actor-map-presence-v2/design.md
   * §D4 for the endpoint contracts (body shape, opt-out 204, rate-limiter
   * 429, snapshot response shape).
   *
   * Coordinates are passed as `[lat, lng]` tuples to mirror the way
   * the design doc writes the contract
   * (`sw: [lat,lng], ne: [lat,lng]`). The snapshot wrapper encodes each
   * tuple as a comma-joined query param (`sw=lat,lng&ne=lat,lng`) so the
   * controller can parse with `split(',')`.
   */
  const mapPresence = {
    /**
     * GET /api/v2/map_presence?sw=lat,lng&ne=lat,lng
     *
     * Returns the current avatar set for the requested viewport — server
     * tessellates to hexes, SUNIONs the reverse-index sets, dedupes
     * `<actor_id>:<tab_id>` strings to unique actor_ids, and batch-loads
     * via `Discussions::ActorMapPayloadSerializer.batch(actor_ids)`.
     *
     * @param {[number, number]} sw - Viewport southwest corner [lat, lng]
     * @param {[number, number]} ne - Viewport northeast corner [lat, lng]
     * @returns {Promise<{ avatars: Array<{ actorId: number, centroid: [number, number], zoom: number, actor: Object }> }>}
     */
    snapshot: async (sw, ne) =>
      get('/map_presence', { query: { sw: `${sw[0]},${sw[1]}`, ne: `${ne[0]},${ne[1]}` } }),

    /**
     * POST /api/v2/map_presence
     *
     * Pan-write: server diffs the requested hex set against the per-tab
     * reverse-index, fires SADD/SREM, refreshes TTLs, and broadcasts
     * upsert/leave_viewport envelopes. Frontend calls this on debounced
     * (150 ms) `moveend`. Returns `204 No Content` if the requester has
     * opted out (`users.map_presence_visible == false`);
     * returns `429` if per-actor rate-limit exceeded.
     *
     * @param {[number, number]} sw - Viewport southwest corner [lat, lng]
     * @param {[number, number]} ne - Viewport northeast corner [lat, lng]
     * @param {number} zoom - Current map zoom (integer)
     * @param {string} tabId - Stable per-tab UUID from `sessionStorage`
     * @returns {Promise<{ ok: boolean, hex_set_size: number }>}
     */
    write: async (sw, ne, zoom, tabId) => {
      return post('/map_presence', {
        sw,
        ne,
        zoom,
        tab_id: tabId,
      })
    },
  }

  /**
   * GET /api/v2/validation_queue/groups/:key — ONE validation-queue group,
   * expanded, as `Api::V2::ValidationQueueController#show_group` builds it
   * (validation-quiet-queue, design D3).
   *
   * THE ONE READ OF A SINGLE GROUP, and it has two callers by design: the
   * review store refreshes the open group through it (a deep link that holds
   * no envelope, and every cable event naming the open BusinessOperation), and
   * the queue probes a deep-link key through it to decide whether to say "no
   * longer in your queue". A second spelling of this path in either caller is a
   * second answer to what a group is.
   *
   * THE 404 IS AN ARM, NOT A FAILURE, and it is deliberately left to throw.
   * `baseFetch` rejects with `error.status === 404` and `error.code` carrying
   * the server's own sentence ("This group is no longer in your queue."), which
   * is what lets a caller tell "this group left your queue" from "the network
   * dropped" — a resolved null would collapse both into one silence.
   *
   * PAGING IS THE CALLER'S LOOP. Expansion is implicit in the action, so an
   * ordinary group's paging param is `expand_page`; `proposals_pages` on the
   * returned group says how many reads a group past the per-group cap takes. A
   * card, or a group above the server's window bound, is read BY WINDOW on a
   * record cursor instead (structural-hop-banding D11): `after` names the
   * window after a cursor the response stated (`proposals_window`), and
   * `focus_proposal_id` the window holding one proposal's row. `opened` is the
   * server's `OPEN_PARAM`: the review store sends it on the first read of an
   * open the validator made, and on no other read.
   *
   * @param {string} key the row's key — a BusinessOperation UUID, the queue's
   *   synthetic `ungrouped_<proposal id>`, or a card's `card_<card id>`
   * @param {Object} [options]
   * @param {number|null} [options.expandPage] 1-based proposals page; omitted
   *   reads the first
   * @param {string|null} [options.after] a record cursor the server stated
   * @param {number|string|null} [options.focusProposalId] the proposal whose
   *   window to answer
   * @param {boolean} [options.opened] this read is a user-initiated open
   * @returns {Promise<{ success: boolean, group: Object, validation_skips: number, dissent_expiry_days: number }>}
   */
  const fetchValidationGroup = async (key, { expandPage, after, focusProposalId, opened } = {}) => {
    // An option left out is left out of the query (`queryString` drops null and
    // undefined), so none needs a default.
    //
    // THE ROUTE'S 404 IS AN ANSWER, never a failure: `#show_group` answers a
    // settled, withdrawn or out-of-relation group with it, and both callers —
    // the queue's landing and the review store's group read — branch on it as
    // "gone". So it is declared here, where the route's contract lives.
    return get(`/validation_queue/groups/${encodeURIComponent(key)}`, {
      query: { expand_page: expandPage, after, focus_proposal_id: focusProposalId, opened: opened === true || null },
      expectedStatuses: [404],
    })
  }

  /**
   * GET /api/v2/revision_contests/:id — ONE revision contest's review source,
   * as `Api::V2::RevisionContestsController#show` projects it (revision-contests
   * RC-D11): `{ success, contest: { id, proposed_change } }`, the focal item
   * under `proposed_change.contest`.
   *
   * THE ONE READ OF A CONTEST, and its caller is the review store, where the
   * contest source's identity lives — the twin of `fetchValidationGroup` for a
   * group source.
   *
   * THE 404 IS AN ARM, NOT A FAILURE, and is left to throw as the group read's
   * is: a contest that does not exist and one the viewer may not see answer the
   * same 404, and the store closes the pane on it.
   *
   * @param {number} contestId the contest's id
   * @returns {Promise<{ success: boolean, contest: { id: number, proposed_change: Object } }>}
   */
  const fetchRevisionContest = async (contestId) =>
    get(`/revision_contests/${encodeURIComponent(contestId)}`)

  /**
   * GET /api/v2/side_quests?filing_id= — ONE curiosity filing's side-quests
   * source, as `Api::V2::SideQuestsController#index` serves it (asc D25, cfw
   * D13): `{ success, filing_id, topic_id, side_quests: [...] }`, each row
   * `Api::V2::SideQuestSerializer`'s, never a value, recipient or answerer.
   *
   * THE ONE READ OF A FILING'S SIDE QUESTS, and its caller is the review store,
   * where the side-quests source's identity lives — the twin of
   * `fetchRevisionContest` for a contest source. A missing or non-positive
   * filing id is a 404 that throws; another Actor's filing, and a filing that
   * offered nothing, answer an empty list with a null `topic_id`.
   *
   * @param {number} filingId the offering ArtemisFiling's id
   * @returns {Promise<{ success: boolean, filing_id: number, topic_id: (number|null), side_quests: Object[] }>}
   */
  const fetchSideQuests = async (filingId) =>
    get('/side_quests', { query: { filing_id: filingId } })

  /**
   * Typed wrappers for a hub's sponsorship — the singleton sub-resource
   * `Api::V2::Discussions::SponsorshipsController` serves under the hub
   * (we-intel-hub-sponsorship design D6) — and for the signed-in user's own
   * sponsorships across hubs (`Api::V2::HubSponsorshipsController`, design
   * D1). THE ONE SPELLING of those calls: the Overview's Artemis section
   * (`HubSponsorshipSection.vue`), a team space's Billing and the account
   * settings' sponsorships list (`HubSponsorships.vue`) all reach the endpoints
   * through here, so no caller builds a path, the cap's body key or the
   * scope's wire spelling.
   *
   * EVERY CALL BUT `list` ACTS ON ONE SCOPE (company-team-private-space D8): a
   * hub, plus optionally one of its team spaces. With no `teamSpaceId` it is
   * the hub's own unscoped row, the one the hub detail's `sponsorship` shows;
   * with one it is that space's row, the one its `viewerTeamSpaces` entry's
   * `sponsorship` shows. The space rides the query string as `team_space_id`
   * on a GET or DELETE (a `teamSpaceId` query is not read, and answers the
   * hub's own row) and the JSON body as `teamSpaceId` on a PATCH or POST. A
   * space whose team the viewer is not on answers 404, as a missing one does.
   *
   * `list` resolves `{ success, data: { sponsorships } }`: one row per live
   * accepted sponsorship the user is the sponsor of — a member sponsorship
   * they accepted, and each one held by a team they primary-own — as
   * `{ id, hubId, hubName, teamSpaceId }` (`id` the row's own, since one hub
   * can hold the user's row and a team space's side by side; `teamSpaceId`
   * null for a hub's own row) beside the sponsor tier's fields; `[]` when
   * none. Every other call resolves `{ success, data: { sponsorship } }`,
   * whose `sponsorship` is tiered by who asks (`HubSponsorshipSerializer`):
   * `state` for anyone who can view the scope; also `allowanceUsedPricingUnits`
   * and `paused` for the scope's admins; also `capPricingUnits`,
   * `drawnThisPeriodPricingUnits` and `earningsToDatePricingUnits` for the
   * sponsor alone. Every figure is integer pricing units.
   *
   * A refusal THROWS, as from any `baseFetch` call, with `error.status` and the
   * parsed body on `error.details`. `accept` answers 409 when the scope is
   * someone else's (the state as the viewer sees it rides
   * `error.details.data.sponsorship`), 403 to an ineligible user, and 404 for a
   * deleted hub or an unknown space.
   *
   * RAISING THE CAP IS STEP-UP GATED; lowering it is not. Nothing here prompts:
   * `baseFetch` answers the `step_up_required` 403 through `useStepUpStore` and
   * replays the PATCH once, as for every gated write. A cancelled
   * confirm-it's-you prompt rejects with `error.code === 'step_up_required'`.
   */
  const hubSponsorshipPath = (hubId) => `/discussions/hubs/${hubId}/sponsorship`
  // A PATCH's or POST's scope: the space in the JSON body, or nothing.
  const teamSpaceBody = (teamSpaceId) => (teamSpaceId == null ? {} : { teamSpaceId })

  const hubSponsorship = {
    /** GET /api/v2/hub_sponsorships — the signed-in user's sponsorships. */
    list: async () => get('/hub_sponsorships'),

    /**
     * GET /api/v2/discussions/hubs/:hub_id/sponsorship — the viewer's tier.
     *
     * @param {number|string} hubId
     * @param {{ teamSpaceId?: number|null }} [scope] the team space, or none
     *   for the hub's own row
     */
    show: async (hubId, { teamSpaceId } = {}) =>
      get(hubSponsorshipPath(hubId), { query: { team_space_id: teamSpaceId } }),

    /**
     * PATCH /api/v2/discussions/hubs/:hub_id/sponsorship — the sponsor sets the
     * monthly cap. Answers the sponsor tier.
     *
     * @param {number|string} hubId
     * @param {number} capPricingUnits a non-negative whole number of pricing units
     * @param {{ teamSpaceId?: number|null }} [scope]
     */
    updateCap: async (hubId, capPricingUnits, { teamSpaceId } = {}) =>
      patch(hubSponsorshipPath(hubId), { capPricingUnits, ...teamSpaceBody(teamSpaceId) }),

    /**
     * POST /api/v2/discussions/hubs/:hub_id/sponsorship/accept — the signed-in
     * user accepts. Answers the state at the viewer's (now the sponsor's) tier.
     *
     * @param {number|string} hubId
     * @param {{ teamSpaceId?: number|null }} [scope]
     */
    accept: async (hubId, { teamSpaceId } = {}) =>
      post(`${hubSponsorshipPath(hubId)}/accept`, teamSpaceBody(teamSpaceId)),

    /**
     * DELETE /api/v2/discussions/hubs/:hub_id/sponsorship — the sponsor (or a
     * sponsoring team's human admin) revokes. Answers the scope's state as the
     * revocation left it, at the viewer tier.
     *
     * @param {number|string} hubId
     * @param {{ teamSpaceId?: number|null }} [scope]
     */
    revoke: async (hubId, { teamSpaceId } = {}) =>
      del(hubSponsorshipPath(hubId), { query: { team_space_id: teamSpaceId } })
  }

  /**
   * GET /api/v2/introduction_proposals/:id/cost — the introduction card's live cost
   * line (`Api::V2::IntroductionProposalsController#cost`, reverse-hunt-matching
   * th-wk397.18) for the share-picker toggles as the sender has them, so the card
   * re-estimates without ever repeating the formula.
   *
   * Resolves `{ success, data: { cost: { recipients, windows, estimate_units,
   * estimate_usd, ceiling_units, ceiling_usd } } }` — the envelope's own `cost`
   * shape. Steering-only while the card is pending: anyone else, and a decided
   * card, is a 404 that THROWS as from any `baseFetch` call.
   *
   * @param {number|string} proposalId the envelope's `proposal_id`
   * @param {string[]} paths the toggled-on decision paths, `"<Type>#<id>.<key>"`
   */
  const introductionProposal = {
    cost: async (proposalId, paths) =>
      get(`/introduction_proposals/${encodeURIComponent(proposalId)}/cost`, { query: { 'paths[]': paths } })
  }

  /**
   * GET /api/v2/introduction_deliveries/:id — the recipient's read of an introduction
   * card (`Api::V2::IntroductionDeliveriesController#show`, reverse-hunt-matching
   * th-wk397.10), addressed by the delivery id the card's registered ask carries.
   *
   * Resolves `{ success, data: { delivery: { id, state, direction, coverage_expires_at,
   * prose, record }, introductions: [{ ordinal, items, ref }] } }`: the recipient's own
   * record (`record`, their chip and facets, or null), and the covered values banded for
   * the recipient, with no identity of the sender's side until they accept. A missing, withdrawn,
   * deleted or not-yours delivery is ONE 404 that THROWS as from any `baseFetch` call.
   *
   * @param {number|string} deliveryId the card's `registered_ask_subject_id`
   */
  const introductionDelivery = {
    show: async (deliveryId) => get(`/introduction_deliveries/${encodeURIComponent(deliveryId)}`)
  }

  return {
    baseFetch,
    get,
    post,
    patch,
    put,
    delete: del,
    mapPresence,
    fetchValidationGroup,
    fetchRevisionContest,
    fetchSideQuests,
    hubSponsorship,
    introductionProposal,
    introductionDelivery
  }
})